import {
  Card,
  Grid,
  Stack,
  TextField,
  FormControlLabel,
  Button,
  Checkbox,
  Avatar,
} from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import AutoComplete from '../../../components/AutoComplete';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AddOnProductVariant as IAddOnProductVariant } from '../../../store/addonProductVariant/api/interface';
import { useFormik } from 'formik';
import {
  createAddOnProductVariant,
  getAddOnProductVariant,
  patchAddOnProductVariant,
} from '../../../store/addonProductVariant/api';
import { setStatus } from '../../../store/addonProductVariant/addonProductVariantSlice';
import { useEffect, useState } from 'react';
import { decryptString, getAssetURl } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { omit, uniqBy } from 'lodash';
// import {
//   setStatus as selectAddOnProductSetStatus,
//   reset as resetSelectAddOnProductReset,
// } from '../../../store/selectAddOnProduct/selectAddOnProductSlice';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import addOnProductVariantSchema from '../../../validation/addOnProductVariantSchema';
import {
  findSelectAddOnCategory,
  searchSelectAddOnCategory,
} from '../../../store/selectAddOnCategory/api';
import { reset as resetAddOnCategory } from '../../../store/selectAddOnCategory/selectAddOnCategorySlice';
import {
  findSelectAddOnProduct,
  searchSelectAddOnProduct,
} from '../../../store/selectAddOnProduct/api';
import { reset as resetAddOnProduct } from '../../../store/selectAddOnProduct/selectAddOnProductSlice';
import { DocumentType } from '../../../enum';
import { uploadAssets } from '../../../utils/uploadAssets';

const AddOnProductForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: any = useParams();
  const location = useLocation();
  const state = location.state as IAddOnProductVariant; // Type Casting, then you can get the params passed via router

  const isAddMode = !params?.id;
  const { isLoading } = useAppSelector((state) => state.addOnProductVariant);
  const { accessToken } = useAppSelector((state) => state.auth);
  const [response, setResponse] = useState<any>();
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  const {
    handleSubmit,
    values,
    setValues,
    handleChange,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
  } = useFormik({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      previewUrl: '',
      stock: '',
      price: '',
      addonCategoryId: '',
      addonProductId: '',
      position: '',
      activeState: true,
    },
    validationSchema: addOnProductVariantSchema,
    onSubmit: async (values) => {
      try {
        if (isAddMode) {
          const reqBody = omit(values, ['id']);
          await dispatch(createAddOnProductVariant(reqBody)).unwrap();
        }
        if (!isAddMode) {
          const reqBody: any = values;
          delete reqBody.createdAt;
          delete reqBody.createdBy;
          delete reqBody.updatedAt;
          delete reqBody.updatedBy;
          delete reqBody.addon_category;
          delete reqBody.addon_product;
          await dispatch(patchAddOnProductVariant(reqBody)).unwrap();
        }
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
        // dispatch(resetSelectAddOnProductReset());
        // dispatch(selectAddOnProductSetStatus({ status: 'idle' }));
        navigation('/addon-product-variant');
      } catch (error: any) {}
    },
  });

  useEffect(() => {
    const fetchById = async () => {
      if (isAddMode) return;

      if (state) {
        setValues(state);
        setResponse(state);
      } else {
        try {
          const decodeParam = decryptString(params.id);
          const record = await dispatch(
            getAddOnProductVariant({ id: decodeParam })
          ).unwrap();
          setValues(record);
          setResponse(record);
        } catch (error: any) {
          navigation('/addon-product-variant');
        }
      }
    };
    fetchById();
  }, [dispatch, isAddMode, navigation, params?.id, setValues, state]);

  // Addon Category Data
  const {
    data: addOnCategoryData,
    isLoading: isAddOnCategoryLoading,
    status: addOnCategoryStatus,
  } = useAppSelector((state) => state.selectAddOnCategory);

  useEffect(() => {
    if (addOnCategoryStatus === 'idle') {
      dispatch(findSelectAddOnCategory({}));
    }
  }, [addOnCategoryStatus]);

  const searchAddOnCategory = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetAddOnCategory());
      return;
    }
    const params = {
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(searchSelectAddOnCategory(params));
  };

  const uniqueAddOnCategoryData = uniqBy(
    [
      ...(response?.addon_category ? [response?.addon_category] : []),
      ...addOnCategoryData,
    ],
    'id'
  );

  // Addon Product  Data
  const {
    data: addOnProductData,
    isLoading: isAddOnProductLoading,
    status: addOnProductStatus,
  } = useAppSelector((state) => state.selectAddOnProduct);

  useEffect(() => {
    if (addOnProductStatus === 'idle') {
      dispatch(findSelectAddOnProduct({}));
    }
  }, [addOnProductStatus]);

  const searchAddOnProduct = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetAddOnProduct());
      return;
    }
    const params = {
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(searchSelectAddOnProduct(params));
  };

  const uniqueAddOnProductData = uniqBy(
    [
      ...(response?.addon_product ? [response?.addon_product] : []),
      ...addOnProductData,
    ],
    'id'
  );

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    field: string
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        documentType,
        accessToken
      );
      if (uploadResult?.data?.id) {
        setFieldValue(field, uploadResult?.data?.keypath);
      }
    }
  };

  return (
    <Grid
      container
      spacing={3}
    >
      <Grid
        item
        xs={12}
        md={12}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <form onSubmit={handleSubmit}>
            <Card sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: {
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  },
                }}
              >
                <AutoComplete
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                    } else if (newValue) {
                      setFieldValue('addonCategoryId', newValue.id);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('addonCategoryId', '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option) => option.labelEn}
                  error={Boolean(
                    touched.addonCategoryId && errors.addonCategoryId
                  )}
                  helperText={
                    Boolean(touched.addonCategoryId && errors.addonCategoryId)
                      ? errors.addonCategoryId
                      : ''
                  }
                  fetchFunction={() => null}
                  label={'Select Add-On Category'}
                  loading={isAddOnCategoryLoading}
                  options={uniqueAddOnCategoryData}
                  searchFunction={searchAddOnCategory}
                  value={
                    uniqueAddOnCategoryData?.filter(
                      (addOnCategory) =>
                        addOnCategory.id === values.addonCategoryId
                    )?.[0] || null
                  }
                />

                <AutoComplete
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                    } else if (newValue) {
                      setFieldValue('addonProductId', newValue.id);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('addonProductId', '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option) => option.labelEn}
                  error={Boolean(
                    touched.addonProductId && errors.addonProductId
                  )}
                  helperText={
                    Boolean(touched.addonProductId && errors.addonProductId)
                      ? errors.addonProductId
                      : ''
                  }
                  fetchFunction={() => null}
                  label={'Select Add-On Product'}
                  loading={isAddOnProductLoading}
                  options={uniqueAddOnProductData}
                  searchFunction={searchAddOnProduct}
                  value={
                    uniqueAddOnProductData?.filter(
                      (addOnProduct) =>
                        addOnProduct.id === values.addonProductId
                    )?.[0] || null
                  }
                />

                <TextField
                  fullWidth
                  label={'Label English'}
                  name='labelEn'
                  id='labelEn'
                  onChange={handleChange}
                  value={values.labelEn}
                  error={Boolean(errors.labelEn && touched.labelEn)}
                  helperText={
                    Boolean(errors.labelEn && touched.labelEn) && errors.labelEn
                  }
                />

                <TextField
                  fullWidth
                  label={'Label Arabic'}
                  name='labelAr'
                  id='labelAr'
                  onChange={handleChange}
                  value={values.labelAr}
                  error={Boolean(errors.labelAr && touched.labelAr)}
                  helperText={
                    Boolean(errors.labelAr && touched.labelAr) && errors.labelAr
                  }
                />

                <Grid
                  container
                  spacing={2}
                >
                  <Grid
                    item
                    xs={values?.previewUrl ? 8.5 : 12}
                    sm={values?.previewUrl ? 8.5 : 12}
                  >
                    <TextField
                      type='file'
                      fullWidth
                      name='previewUrl'
                      id='previewUrl'
                      onChange={(e) =>
                        handleFileChange(
                          e as React.ChangeEvent<HTMLInputElement>,
                          DocumentType.AddonVariant,
                          'previewUrl'
                        )
                      }
                      inputProps={{ multiple: false, accept: 'image/*' }}
                      InputLabelProps={{ shrink: true }}
                      variant='outlined'
                      label={'Preview URL'}
                      error={Boolean(errors.previewUrl && touched.previewUrl)}
                      helperText={
                        Boolean(errors.previewUrl && touched.previewUrl) &&
                        errors.previewUrl
                      }
                    />
                  </Grid>
                  {values?.previewUrl && (
                    <Grid
                      item
                      xs={3.5}
                      sm={3.5}
                    >
                      <Avatar
                        alt={values?.labelEn || 'addon'}
                        src={getAssetURl(values?.previewUrl, assetUrl)}
                        sx={{
                          borderRadius: 'unset',
                          m: 1,
                          objectFit: 'contain',
                          '& img': { objectFit: 'contain' },
                          width: { xs: '75px', sm: '100px' },
                          height: 50,
                        }}
                      />
                    </Grid>
                  )}
                </Grid>

                <TextField
                  fullWidth
                  label={'Stock'}
                  name='stock'
                  id='stock'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.stock}
                  error={Boolean(errors.stock && touched.stock)}
                  helperText={
                    Boolean(errors.stock && touched.stock) && errors?.stock
                  }
                />

                <TextField
                  fullWidth
                  label={'Price'}
                  name='price'
                  id='price'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.price}
                  error={Boolean(errors.price && touched.price)}
                  helperText={
                    Boolean(errors.price && touched.price) && errors?.price
                  }
                />

                <TextField
                  fullWidth
                  label={'Position'}
                  name='position'
                  id='position'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.position}
                  error={Boolean(errors.position && touched.position)}
                  helperText={
                    Boolean(errors.position && touched.position) &&
                    errors?.position
                  }
                />
              </Box>

              <Box
                sx={{
                  py: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      color='primary'
                      name='activeState'
                      onChange={(e) =>
                        setFieldValue('activeState', e.target.checked)
                      }
                      checked={values.activeState}
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                    />
                  }
                  label={'Active Status'}
                />
              </Box>

              <Stack
                alignItems='center'
                sx={{ mt: 3 }}
                direction='row'
                justifyContent='flex-end'
                gap={1}
              >
                <Button
                  variant='outlined'
                  onClick={() => navigation('/addon-product-variant')}
                >
                  {'Cancel'}
                </Button>

                <LoadingButton
                  type='submit'
                  variant='contained'
                  loading={isSubmitting}
                >
                  {isAddMode
                    ? 'Add Add-On Product Variant'
                    : 'Edit Add-On Product Variant'}
                </LoadingButton>
              </Stack>
            </Card>
          </form>
        )}
      </Grid>
    </Grid>
  );
};

export default AddOnProductForm;
